/** @format */

import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import CardBody from '../../../../components/Card/CardBody';
import I18n from '../../../../components/Translate/I18n';
import GridContainer from '../../../../components/Grid/GridContainer';
import GridItem from '../../../../components/Grid/GridItem';
import { Card, Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel, Radio, RadioGroup } from '@material-ui/core';
import CustomInputExt from '../../../../components/CustomInput/CustomInputExt';
import {
    SimCardServiceTypeAllowedParamMap,
    SimCardServiceTypeAllowedParamMapAllowedValue,
    SimCardServiceTypeSingleParam,
} from '../../../../interfaces/collections/SimCardServiceInterface';
import { BoolTranslateTrueFalseHelper } from '../../../../helpers/BoolTranslateHelper';
import CustomTextButtom from '../../../../components/Buttons/CustomTextButton';

const _ = require('underscore');

type OplSimCardServiceTypeParamItemSdgFormItemType = {
    initialParam: SimCardServiceTypeSingleParam;
    save(serviceParams: SimCardServiceTypeSingleParam): void;
};

export const SdgBmeFormControlCheckBox = (v: {
    value: string;
    label: string;
    type: string;
    selectedElement: Array<SimCardServiceTypeAllowedParamMapAllowedValue>;
    updateAllowedValue(newAllowedValue: Array<SimCardServiceTypeAllowedParamMapAllowedValue>): void;
    readonly?: boolean;
}): React.ReactElement => {
    let check = false;
    const elem = _.find(v.selectedElement, (item: any) => {
        return item.value === v.value;
    });
    if (elem && elem.value) {
        check = true;
    }
    const [checked, setChecked] = useState(check);
    const onCheckBoxEvent = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const checked = event.target.checked;
        const value = event.target.value;
        const elements: Array<SimCardServiceTypeAllowedParamMapAllowedValue> = [];
        for (let i = 0; i < v.selectedElement.length; i++) {
            if (v.selectedElement[i].value?.toString() !== value && value !== 'true' && value !== 'false') {
                elements.push({ ...v.selectedElement[i] });
            } else if (typeof v.selectedElement[i].value === 'boolean' && v.value !== value && v.value === 'true') {
                elements.push({ value: true });
            } else if (typeof v.selectedElement[i].value === 'boolean' && v.value !== value && v.value === 'false') {
                elements.push({ value: false });
            }
        }
        if (checked) {
            if (value === 'true') {
                elements.push({ value: true });
            } else if (value === 'false') {
                elements.push({ value: false });
            } else if (v.type === 'number') {
                elements.push({ value: parseInt(value) });
            } else {
                elements.push({ value: value });
            }
        }
        setChecked(checked);
        v.updateAllowedValue(elements);
    };
    if (!v.readonly) {
        return (
            <FormControlLabel
                control={<Checkbox checked={checked} name={'allowed_check_box_' + v.value} onChange={onCheckBoxEvent} value={v.value} />}
                label={<I18n trans={v.label} />}
            />
        );
    } else {
        return (
            <React.Fragment>
                {checked && (
                    <React.Fragment>
                        <I18n trans={v.label} />
                        <br />
                    </React.Fragment>
                )}
            </React.Fragment>
        );
    }
};

export const SetCustomAmountValues = (name: string, updateAllowedValue: (value: number | string) => void): React.ReactElement => {
    const [amount, setAmount] = useState<number>(0);
    const onChangeEvent = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        const field = event.target.name;
        setAmount(parseInt(value));
    };
    const update = (): void => {
        updateAllowedValue(amount);
        setAmount(0);
    };
    return (
        <GridItem xs={12}>
            <GridContainer>
                <GridItem xs={12}>
                    <CustomInputExt
                        formControlProps={{ fullWidth: true }}
                        required={true}
                        labelText="prov:feature.amount"
                        name={name}
                        value={amount}
                        onChange={onChangeEvent}
                    />
                </GridItem>
                <GridItem xs={12}>
                    <CustomTextButtom callback={update} label="prov:feature.add_custom_amount" size="sm" />
                </GridItem>
            </GridContainer>
        </GridItem>
    );
};

export const OplSimCardServiceTypeParamItemSdgBmeFormItem: FunctionComponent<OplSimCardServiceTypeParamItemSdgFormItemType> = ({
    initialParam,
    save,
}): React.ReactElement => {
    const [raised, setRaised] = useState(false);
    const componentParam = useRef<SimCardServiceTypeSingleParam>(initialParam);
    const [required, setRequired] = useState(componentParam.current.params.required);
    const [defaultValue, setDefaultValue] = useState<string | number | boolean | Array<string> | Array<number> | Array<boolean> | null>(
        componentParam.current.params.default
    );
    const [allowedValue, setAllowedValue] = useState<Array<SimCardServiceTypeAllowedParamMapAllowedValue>>(
        componentParam.current.params.allowedValue ?? []
    );
    const [globalAllowedValue, setGlobalAllowedValue] = useState<Array<SimCardServiceTypeAllowedParamMapAllowedValue>>(
        componentParam.current.globalParams.allowedValue ?? []
    );

    const onCheckBoxEvent = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const name = event.target.name;
        const value = event.target.value;
        if (name === 'required') {
            setRequired(event.target.checked);
            componentParam.current.params.required = event.target.checked;
        }
        if (name === 'defaultValue') {
            if (value === 'true') {
                setDefaultValue(true);
                componentParam.current.params.default = true;
            } else if (value === 'false') {
                setDefaultValue(false);
                componentParam.current.params.default = false;
            } else if (value === 'null') {
                setDefaultValue(value);
                componentParam.current.params.default = null;
            } else {
                setDefaultValue(value);
                componentParam.current.params.default = value;
            }
        }
    };

    const allowedValuesUpdate = (element: Array<SimCardServiceTypeAllowedParamMapAllowedValue>): void => {
        setAllowedValue(element);
        setDefaultValue('null');
        componentParam.current.params.allowedValue = element;
        componentParam.current.params.default = null;
    };

    const addAllowedValue = (value: number | string): void => {
        if (
            !_.find(componentParam.current.params.allowedValue, (obj: SimCardServiceTypeAllowedParamMapAllowedValue) => {
                return value === obj.value;
            })
        ) {
            if (!componentParam.current.globalParams.allowedValue) {
                componentParam.current.globalParams.allowedValue = [];
            }
            componentParam.current.globalParams.allowedValue?.push({ value: value, removeAllowed: true });
            setGlobalAllowedValue(componentParam.current.globalParams.allowedValue);
            setAllowedValue([]);
            setDefaultValue('null');
        }
    };

    const onTextEvent = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setDefaultValue(event.target.value);
    };

    const onNumberEvent = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setDefaultValue(parseInt(event.target.value));
    };

    const defaultSelectRadioValues = (defaultValues: SimCardServiceTypeAllowedParamMap): React.ReactElement => {
        if (!defaultValues.input && defaultValues.type === 'string') {
            return (
                <GridItem xs={6}>
                    <CustomInputExt required={false} labelText="prov:feature.default" name="default" value={defaultValue} onChange={onTextEvent} />
                </GridItem>
            );
        }
        if (!defaultValues.input && defaultValues.type === 'number') {
            return (
                <GridItem xs={6}>
                    <CustomInputExt required={false} labelText="prov:feature.default" name="default" value={defaultValue} onChange={onNumberEvent} />
                </GridItem>
            );
        }
        if (defaultValues.input === 'select' || defaultValues.input === 'bool') {
            return (
                <GridItem xs={6} key={'form_control_default_select_radio_name' + componentParam.current.paramName + '_parameters'}>
                    <FormControl component="fieldset">
                        <FormLabel>
                            <I18n trans="prov:feature.default" />
                        </FormLabel>
                        <RadioGroup aria-label="prov:feature.default" name="defaultValue" value={defaultValue} onChange={onCheckBoxEvent}>
                            {defaultValues.allowedValue?.map((item) => {
                                if (typeof item.value === 'boolean') {
                                    return (
                                        <FormControlLabel
                                            key={'default_radio_element_' + item.value ? 'true' : 'false'}
                                            value={item.value ? 'true' : 'false'}
                                            control={<Radio />}
                                            label={<I18n trans={BoolTranslateTrueFalseHelper(item.value)} />}
                                        />
                                    );
                                } else {
                                    return (
                                        <FormControlLabel
                                            key={'default_radio_element_' + item.value}
                                            value={item.value?.toString()}
                                            control={<Radio />}
                                            label={item.value}
                                        />
                                    );
                                }
                            })}
                            <FormControlLabel
                                key={'default_radio_element_null_value'}
                                value={'null'}
                                control={<Radio />}
                                label={<I18n trans="prov:feature.no_set_value" />}
                            />
                        </RadioGroup>
                    </FormControl>
                </GridItem>
            );
        }
        return <div />;
    };

    const saveOn = useCallback((): void => {
        save(componentParam.current);
    }, [save]);

    useEffect(() => {
        saveOn();
    }, [defaultValue, allowedValue, required, saveOn]);

    const allowedValues = (): React.ReactElement => {
        let element = globalAllowedValue.map((item) => {
            if (!item.value) {
                return null;
            }
            if (typeof item.value === 'boolean') {
                return (
                    <SdgBmeFormControlCheckBox
                        key={'form_control_check_box' + BoolTranslateTrueFalseHelper(item.value)}
                        value={item.value ? 'true' : 'false'}
                        label={BoolTranslateTrueFalseHelper(item.value)}
                        selectedElement={allowedValue}
                        updateAllowedValue={allowedValuesUpdate}
                        type="bool"
                    />
                );
            }
            return (
                <SdgBmeFormControlCheckBox
                    key={'form_control_check_box' + item.value.toString()}
                    value={item.value.toString()}
                    label={item.value.toString()}
                    selectedElement={allowedValue}
                    updateAllowedValue={allowedValuesUpdate}
                    type={typeof item.value}
                />
            );
        });
        return (
            <React.Fragment>
                <GridItem xs={6}>
                    <I18n trans="prov:feature.allowed_value" />
                    <FormGroup row>{element}</FormGroup>
                </GridItem>
            </React.Fragment>
        );
    };
    return (
        <Card style={{ marginBottom: '10px' }} raised={raised} onMouseOver={(): void => setRaised(true)} onMouseOut={(): void => setRaised(false)}>
            <CardBody>
                <I18n trans="prov:feature.param_name" valueObject={{ param_name: componentParam.current.paramName }} />
                <GridContainer>
                    <GridItem xs={12}>
                        <FormControlLabel
                            control={<Checkbox name="required" checked={required} onChange={onCheckBoxEvent} />}
                            label={<I18n trans="prov:feature.required" />}
                        />
                    </GridItem>
                    {allowedValue && defaultSelectRadioValues(componentParam.current.params)}
                    {globalAllowedValue.length > 0 && allowedValues()}
                    {componentParam.current.globalParams.allowedType &&
                        componentParam.current.globalParams.allowedType === 'array.defined' &&
                        SetCustomAmountValues(componentParam.current.paramName, addAllowedValue)}
                </GridContainer>
            </CardBody>
        </Card>
    );
};
